import { PureComponent } from "react";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { ProviderSet } from "@app/provider-sets/providerSet";
import { ProviderSetType } from "@app/provider-sets/enumerations";
import { Link } from "react-router-dom";
import { FlyoutContextOutgoingProps, FlyoutFooter, withFlyoutContext } from "@app/shared/Flyouts";
import ProjectInfoSummary from "@app/projects/projectInfoSummary";
import ProjectService from "@app/projects/projectService";
import { QESButton } from "@app/shared/qes-standards/Button";

interface ArchivePanelProps {
  providerSet: ProviderSet
  loading: boolean;
  handlePurge: () => void;
}

interface ArchivePanelState {
  associatedProjects: ProjectInfoSummary[];
  filteredProviders: ProviderSet[];
  loading: boolean;
}

// this needs to be a class component to inherit the onClose prop from its intended parent, SidePanel
class WrappedPurgePanelContent extends PureComponent<ArchivePanelProps & FlyoutContextOutgoingProps, ArchivePanelState> {
  constructor(props: ArchivePanelProps & FlyoutContextOutgoingProps) {
    super(props);
    this.state = {
      associatedProjects: [],
      filteredProviders: [],
      loading: (true)
    };
  }

  private projectService = new ProjectService();

  public async componentDidMount() {
    if (!this.props.providerSet)
      return;

    let assocProjects: ProjectInfoSummary[] = [];
    if (this.props.providerSet.providerSetType === ProviderSetType.Standard || this.props.providerSet.providerSetType === ProviderSetType.AccessReporting) {
      assocProjects = await this.projectService.getProjectSummaryByProviderSetIdAsync(this.props.providerSet.resource.resourceId);
    } else {
      assocProjects = await this.projectService.getV1ProjectByProviderSetIdAsync(this.props.providerSet.resource.resourceId, this.props.providerSet.providerSetType);
    }

    this.setState({
      associatedProjects: assocProjects,
      filteredProviders: (!!this.props.providerSet.filteredProviderSets ? this.props.providerSet.filteredProviderSets.filter(x => !x.resource.datePurged) : []),
      loading: (this.props.loading)
    });

  }

  public render() {
    return (
      <>
        {this.state.loading ? <LoadingSpinner /> :
          <>
            {this.state.associatedProjects.length > 0 || this.state.filteredProviders.length > 0 ?
              <>
                <p className="fs20" >
                  <strong>Cannot Continue</strong>
                </p>
                <p className="mt-2 fs16"><i className="fa fa-exclamation-triangle text-danger me-1"></i>This provider network cannot be purged because it is linked to the following:</p>
              </>
              : null
            }
            {this.state.filteredProviders.length > 0 ?
              <>
                <p className="fs18 mb-1">
                  Associated Filtered Networks:
                </p>
                <ul className="list-unstyled ms-4">
                  {this.props.providerSet.filteredProviderSets.map(x =>
                    <li key={x.resource.resourceId}>
                      <i className="fa fa-chain me-2"></i><Link to={{ pathname: `/provider-sets/${x.resource.resourceId}/detail` }} >{x.resource.name}</Link>
                    </li>)}
                </ul>
              </> : null
            }
            {this.state.associatedProjects.length > 0 ?
              <>
                <p className="fs18 mb-1">
                  Associated Projects:
                </p>
                <ul className="list-unstyled ms-4">
                  {this.state.associatedProjects.map(x =>
                    <li key={x.projectId}>
                      <i className="fa fa-chain me-2"></i>
                      {this.props.providerSet.providerSetType === ProviderSetType.Standard ?
                        <Link to={{ pathname: `/projects/${x.projectId}` }} >{x.name}</Link> :
                        <a href={`${window.uiEndpoint}/${this.props.providerSet.providerSetType}/projects/${x.projectId}`} >{x.name}</a>
                      }
                    </li>)}
                </ul>
              </> : null
            }
            {this.state.associatedProjects.length === 0 && this.state.filteredProviders.length === 0 ?
              <>
                <p className="fs20" >
                  <strong>Warning</strong>
                </p>
                <p className="fs18">
                  This will completely destroy the network and all versions.
                </p>
              </>
              : null
            }
            <FlyoutFooter
              submitButton={
                <QESButton
                  qesTestId="purge-provider-set-panel-content__confirm"
                  className="me-2"
                  qesType="primary-danger"
                  disabled={this.state.associatedProjects.length > 0 || this.state.filteredProviders.length > 0}
                  onClick={() => { this.props.handleCloseFlyout().then(() => { this.props.handlePurge(); }) }}
                >Purge</QESButton>
              }
            />
          </>
        }
      </>
    );
  }
}

const PurgePanelContent = withFlyoutContext(WrappedPurgePanelContent);
export default PurgePanelContent;
